import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { getPersistedReferralPartnerCode } from '../lib/referralPartner';
import { sendSignupOtp, verifySignupOtp } from '../lib/signupEmailVerification';
import { getExtendedSignupAnswers, getCurrentSignupFlow, trackSignupEvent, trackSignupEventOnce } from '../lib/signupAnalytics';
import { notifySignupAutomationWebhook } from '../lib/signupAutomationWebhook';
import { isPasswordStrongEnough } from '../lib/passwordStrength';

export interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  businessName: string;
  businessEmail: string;
  businessPhone: string;
  businessAddress: string;
}

const EMPTY_FORM: SignupFormData = {
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  businessName: '',
  businessEmail: '',
  businessPhone: '',
  businessAddress: '',
};

/**
 * Shared business-owner signup logic (account + business creation), extracted so
 * the standard signup page and the referral landing page use identical behavior.
 * Presentation lives in SignupForm; this hook owns state, validation, and submit.
 */
export function useSignup(options?: {
  initialEmail?: string;
  /** Seed password + confirm (e.g. Signup-nexus landing already collected them). */
  initialPassword?: string;
  /** Email already verified by the extended flow's own OTP screens — locks it in and skips re-verification. */
  initialVerifiedEmail?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const seededPassword = options?.initialPassword ?? '';
  const [formData, setFormData] = useState<SignupFormData>({
    ...EMPTY_FORM,
    email: options?.initialVerifiedEmail?.trim().toLowerCase() || options?.initialEmail?.trim() || '',
    password: seededPassword,
    confirmPassword: seededPassword,
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Email verification (between step 1 and step 2)
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(
    options?.initialVerifiedEmail?.trim().toLowerCase() || null,
  );
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [emailInUse, setEmailInUse] = useState(false);

  const navigate = useNavigate();
  const { handleSuccessfulLogin } = useBusiness();

  const validateStep1 = () => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (!isPasswordStrongEnough(formData.password)) {
      errors.password = 'Password doesn\u2019t meet the requirements below';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.values(errors).every((e) => e === '');
  };

  const validateStep2 = () => {
    const errors: Record<string, string> = {};

    // Names are collected on step 2 alongside the business details.
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';

    if (!formData.businessName.trim()) errors.businessName = 'Business name is required';

    if (!formData.businessEmail?.trim()) {
      errors.businessEmail = 'Business email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.businessEmail)) {
      errors.businessEmail = 'Please enter a valid email address';
    }

    if (!formData.businessPhone?.trim()) errors.businessPhone = 'Business phone is required';

    setValidationErrors(errors);
    return Object.values(errors).every((e) => e === '');
  };

  /**
   * Extended-flow hook-in: its own screens verify the email via OTP, then hand
   * the verified address to this hook so submit can run without re-verifying.
   */
  const prefillVerifiedEmail = (email: string) => {
    const normalized = email.trim().toLowerCase();
    // businessEmail tracks the verified email (the extended flow locks it).
    setFormData((prev) => ({ ...prev, email: normalized, businessEmail: normalized }));
    setVerifiedEmail(normalized);
  };

  const proceedToStep2 = () => {
    // The step-2 email field is locked to the verified account email.
    setFormData((prev) => ({ ...prev, businessEmail: prev.email }));
    setCurrentStep(2);
    trackSignupEventOnce('step_view:business', {
      eventType: 'step_view',
      step: 'business',
      referralCode: getPersistedReferralPartnerCode(),
    });
  };

  // Step 1 → email verification → step 2.
  const handleNextStep = async () => {
    if (currentStep !== 1 || !validateStep1()) return;
    const email = formData.email.trim().toLowerCase();

    // Already verified this exact email → skip straight to business info.
    if (verifiedEmail && verifiedEmail === email) {
      proceedToStep2();
      return;
    }

    setSendingCode(true);
    setError(null);
    setEmailInUse(false);
    try {
      const res = await sendSignupOtp(email);
      if (res.available === false) {
        setEmailInUse(true);
        trackSignupEvent({
          eventType: 'error',
          step: 'account',
          data: { reason: res.reason === 'team_member' ? 'team_member_email' : 'email_in_use' },
          referralCode: getPersistedReferralPartnerCode(),
        });
        setValidationErrors((prev) => ({
          ...prev,
          email:
            res.reason === 'team_member'
              ? 'This email belongs to a team member account. Log in instead, or use a different email to create a new business.'
              : 'An account with this email already exists',
        }));
        return;
      }
      if (res.error === 'throttled') {
        // A code was already sent recently — go to the verify screen anyway.
        setAwaitingVerification(true);
        setCodeError(null);
        return;
      }
      if (!res.ok) {
        setError('Could not send a verification code. Please try again.');
        return;
      }
      setCode('');
      setCodeError(null);
      setAwaitingVerification(true);
      // Step 1 done → verification screen. First funnel milestone after landing.
      // Email rides along so the super-admin signup log can identify the visitor.
      trackSignupEventOnce('step_complete:account', {
        eventType: 'step_complete',
        step: 'account',
        data: { email },
        referralCode: getPersistedReferralPartnerCode(),
      });
      trackSignupEventOnce('step_view:verify', {
        eventType: 'step_view',
        step: 'verify',
        referralCode: getPersistedReferralPartnerCode(),
      });
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    const email = formData.email.trim().toLowerCase();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError('Enter the 6-digit code');
      return;
    }
    setVerifying(true);
    setCodeError(null);
    try {
      const res = await verifySignupOtp(email, trimmed);
      if (res.ok) {
        setVerifiedEmail(email);
        setAwaitingVerification(false);
        setCode('');
        trackSignupEventOnce('step_complete:verify', {
          eventType: 'step_complete',
          step: 'verify',
          referralCode: getPersistedReferralPartnerCode(),
        });
        proceedToStep2();
        return;
      }
      const map: Record<string, string> = {
        expired: 'That code expired. Tap resend for a new one.',
        too_many: 'Too many attempts. Tap resend for a new code.',
        no_code: 'No code found — tap resend.',
        invalid: 'Incorrect code. Please try again.',
      };
      trackSignupEvent({
        eventType: 'error',
        step: 'verify',
        data: { reason: `otp_${res.error || 'invalid'}` },
        referralCode: getPersistedReferralPartnerCode(),
      });
      setCodeError(map[res.error || ''] || 'Incorrect code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    const email = formData.email.trim().toLowerCase();
    setSendingCode(true);
    setCodeError(null);
    try {
      const res = await sendSignupOtp(email);
      if (res.error === 'throttled') {
        setCodeError(`Please wait ${res.retryAfter ?? 30}s before resending.`);
      } else if (res.available === false) {
        setEmailInUse(true);
        setAwaitingVerification(false);
        setValidationErrors((prev) => ({
          ...prev,
          email:
            res.reason === 'team_member'
              ? 'This email belongs to a team member account. Log in instead, or use a different email to create a new business.'
              : 'An account with this email already exists',
        }));
      } else if (!res.ok) {
        setCodeError('Could not resend. Please try again.');
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleBackFromVerify = () => {
    setAwaitingVerification(false);
    setCodeError(null);
  };

  const handlePrevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;

    setLoading(true);
    setError(null);

    try {
      if (!supabase) throw new Error('Supabase client not initialized');

      // 1. Create user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('No user returned from signup');

      // 2. Create business (phone/address trimmed; empty string stored as null).
      //    The persisted referral code (?ref=) becomes immutable first-touch attribution.
      const referralCode = getPersistedReferralPartnerCode();
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .insert([{
          owner_id: authData.user.id,
          name: formData.businessName,
          email: (formData.businessEmail || formData.email)?.trim() || null,
          phone: formData.businessPhone?.trim() || null,
          address: formData.businessAddress?.trim() || null,
          currency: 'CAD',
          subscription_status: 'inactive', // Activated on payment
          ...(referralCode ? { referral_partner_code: referralCode } : {}),
        }])
        .select()
        .single();

      if (businessError) throw businessError;

      // 3. Update user metadata with business ID
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { owned_business_ids: [businessData.id] },
      });
      if (metadataError) {
        console.warn('Failed to update user metadata, but business was created:', metadataError);
      }

      // 3b. Refresh session so BusinessContext sees the new metadata
      await supabase.auth.refreshSession();

      // 4. Update user profile with names
      const { error: userError } = await supabase
        .from('users')
        .update({ first_name: formData.firstName, last_name: formData.lastName })
        .eq('id', authData.user.id);
      if (userError) throw userError;

      // 5. Set setup status to "pending"
      const { error: setupError } = await supabase
        .from('user_setup_status')
        .insert([{
          user_id: authData.user.id,
          setup_step: 'pending',
          current_step: 1,
          completed: false,
        }]);
      if (setupError) {
        console.warn('Failed to create setup status, but account was created:', setupError);
      }

      // Funnel end: the account + business exist. Extended-flow qualifying
      // answers ride along so the business can be qualified from the event.
      const qualifyingAnswers = getExtendedSignupAnswers();
      trackSignupEvent({
        eventType: 'signup_completed',
        step: 'completed',
        data: {
          business_id: businessData.id,
          email: formData.email.trim().toLowerCase(),
          business_name: formData.businessName,
          ...(qualifyingAnswers ? { qualifying_answers: qualifyingAnswers } : {}),
        },
        referralCode: referralCode,
      });

      // Welcome email (fire-and-forget — must never block or fail the signup).
      void supabase.functions
        .invoke('send-welcome-email', {
          body: { firstName: formData.firstName?.trim() || '', businessName: formData.businessName?.trim() || '' },
        })
        .then(({ error: welcomeErr }) => {
          if (welcomeErr) console.warn('Welcome email failed (non-critical):', welcomeErr);
        });

      // Nexus signup → automation webhook (same as website leads; never block signup).
      notifySignupAutomationWebhook({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.businessPhone,
        business_name: formData.businessName,
        business_email: formData.businessEmail || formData.email,
        business_address: formData.businessAddress,
        business_id: businessData.id,
        source: 'account_signup',
        referral_partner_code: referralCode,
        signup_flow: typeof window !== 'undefined' ? window.location.pathname : null,
        qualifying_answers: qualifyingAnswers,
      });

      // 6. Load the newly created business into context
      handleSuccessfulLogin();

      // 7. Nexus: cinematic preparing screen → Growth 10-day trial CTA.
      //    Other flows: classic plan picker.
      if (getCurrentSignupFlow() === 'nexus') {
        navigate('/signup-nexus/preparing', {
          state: {
            userId: authData.user.id,
            businessId: businessData.id,
            email: formData.email,
            fromSignup: true,
          },
        });
      } else {
        navigate('/admin-subscription-management', {
          state: {
            userId: authData.user.id,
            businessId: businessData.id,
            email: formData.email,
            fromSignup: true,
            message: 'Account created successfully! Please select and activate your subscription to continue.',
          },
        });
      }
    } catch (err) {
      console.error('Signup error:', err);
      let errorMessage = 'Failed to create account';
      if (err instanceof Error) {
        if (err.message.includes('already registered')) {
          errorMessage = 'An account with this email already exists';
        } else if (err.message.includes('password')) {
          errorMessage = 'Password must be at least 6 characters';
        } else {
          errorMessage = err.message;
        }
      }
      trackSignupEvent({
        eventType: 'error',
        step: 'business',
        data: { reason: 'submit_failed', message: errorMessage },
        referralCode: getPersistedReferralPartnerCode(),
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    validationErrors,
    currentStep,
    loading,
    error,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    // Email verification
    awaitingVerification,
    code,
    setCode,
    codeError,
    sendingCode,
    verifying,
    emailInUse,
    handleVerifyCode,
    handleResendCode,
    handleBackFromVerify,
    prefillVerifiedEmail,
  };
}

export type UseSignupReturn = ReturnType<typeof useSignup>;
